import { useEffect, useMemo, useState } from "react";
import type { Edge, Node } from "@xyflow/react";
import type { OperationCanvasNodeData } from "../OperationCanvasNode.js";
import { apiFetch } from "../../lib/api.js";
import { NODE_LABELS } from "../../lib/operationPorts.js";
import { loopPairName, resolveLoopPairing } from "../../lib/loopTopology.js";
import { NodeOutputStats, type NodeOutputRecord } from "./nodeInspectorWidgets.js";

/**
 * Loop Start inspector.
 *
 * Autosaves through `setNodeData`, debounced by the editor. No Save button —
 * the editor owns persistence and gating on "save first" is not this product's
 * behaviour.
 *
 * What is deliberately NOT here: the collection binding. Which list a loop
 * iterates is a CONNECTION, made on the canvas by wiring a step into the
 * items port. Offering a second way to set it in the inspector would be two
 * controls for one fact, and the canvas one is the honest one.
 *
 * What IS here is which column of that list identifies an item — a pick from
 * the list's own columns, never typed. It reads the same `/upstream-columns`
 * endpoint every other bound column picker in this app reads.
 */
export function OperationInspectorLoopStart({
  selectedNode,
  operationId,
  setNodeData,
  nodes,
  edges,
  nodeOutputRecord,
  onRunNode,
  runBusy,
  operationStatus,
}: {
  selectedNode: Node<OperationCanvasNodeData>;
  operationId: string;
  setNodeData: (nodeId: string, updater: (data: OperationCanvasNodeData) => OperationCanvasNodeData) => void;
  nodes: Node<OperationCanvasNodeData>[];
  edges: Edge[];
  nodeOutputRecord?: NodeOutputRecord;
  onRunNode: (nodeId: string) => void | Promise<void>;
  runBusy: boolean;
  operationStatus: string;
}) {
  const config = selectedNode.data.config ?? {};
  const maxIterations = typeof config.max_iterations === "number" ? config.max_iterations : null;
  const onItemError = config.on_item_error === "halt" ? "halt" : "continue";
  const abortAfter =
    typeof config.abort_after_consecutive_failures === "number" ? config.abort_after_consecutive_failures : 3;
  const identityColumn = typeof config.identity_column === "string" ? config.identity_column : "";

  const update = (patch: Record<string, unknown>) =>
    setNodeData(selectedNode.id, (data) => ({ ...data, config: { ...data.config, ...patch } }));

  const graph = useMemo(
    () => ({
      nodes: nodes.map((n) => ({ id: n.id, type: n.data.nodeType, config: n.data.config })),
      edges: edges.map((e) => ({ edge_type: "data", from_node: e.source, to_node: e.target })),
    }),
    [nodes, edges]
  );

  const pairing = useMemo(() => resolveLoopPairing(graph), [graph]);
  const pair = pairing.pairs.find((p) => p.startId === selectedNode.id) ?? null;
  const endLabel = pair
    ? nodes.find((n) => n.id === pair.endId)?.data.label?.trim() || NODE_LABELS.loop_end
    : null;

  // The list is a connection, so "what am I looping over?" is answered by
  // naming the connected step — by LABEL, never by a node id.
  const collectionEdge = edges.find((e) => e.target === selectedNode.id);
  const collectionSource = collectionEdge ? nodes.find((n) => n.id === collectionEdge.source) : null;
  const collectionLabel = collectionSource
    ? collectionSource.data.label?.trim() || NODE_LABELS[collectionSource.data.nodeType]
    : null;

  const maxInvalid = maxIterations !== null && (!Number.isInteger(maxIterations) || maxIterations < 1);

  // The list's own columns. Cache-only on the hub — reading them never runs
  // anything — so an unrun upstream yields an empty picker rather than a wait.
  const [collectionColumns, setCollectionColumns] = useState<string[]>([]);
  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const data = await apiFetch<{ columns?: Array<{ name?: string }> }>(
          `/api/tenant/operations/${encodeURIComponent(operationId)}/nodes/${encodeURIComponent(selectedNode.id)}/upstream-columns`,
        );
        if (!live) return;
        setCollectionColumns(
          (data.columns ?? []).map((c) => c.name).filter((n): n is string => typeof n === "string" && !!n.trim()),
        );
      } catch {
        if (live) setCollectionColumns([]);
      }
    })();
    return () => {
      live = false;
    };
  }, [operationId, selectedNode.id, collectionLabel]);

  // Three states, and keeping them apart matters: a bound column the list
  // still has, a bound column the list no longer has (the stale binding the
  // engine refuses at run time — say so HERE, where it can still be fixed),
  // and a bound column we cannot check because nothing upstream has run yet.
  //
  // The third is NOT the same as "nothing chosen", and conflating them showed
  // a loop bound to `title` as though it were unset — a control that quietly
  // contradicts the config it is bound to.
  const columnsKnown = collectionColumns.length > 0;
  const identityListed = identityColumn !== "" && collectionColumns.includes(identityColumn);
  const identityMissing = identityColumn !== "" && columnsKnown && !identityListed;
  const identityUnverified = identityColumn !== "" && !columnsKnown;

  return (
    <div style={{ display: "grid", gap: 12 }}>
      <div className="card">
        <div style={{ fontWeight: 600, marginBottom: 8 }}>{loopPairName(selectedNode.data.label, "start")}</div>

        <div style={{ display: "grid", gap: 10 }}>
          <div style={{ fontSize: 13 }}>
            Items from:{" "}
            {collectionLabel ? (
              <strong>{collectionLabel}</strong>
            ) : (
              <span className="muted">nothing connected yet — wire the list into this step on the canvas</span>
            )}
          </div>

          <div style={{ fontSize: 13 }}>
            Closed by:{" "}
            {endLabel ? (
              <strong>{endLabel}</strong>
            ) : (
              <span className="muted">no loop end yet — a loop needs both ends</span>
            )}
          </div>

          <label className="field">
            <span className="label">Most items to accept</span>
            <input
              className="input"
              type="number"
              min={1}
              step={1}
              value={maxIterations ?? ""}
              onChange={(e) => {
                const v = e.target.value.trim();
                update({ max_iterations: v === "" ? null : Number(v) });
              }}
            />
          </label>
          <div className="help">
            {maxInvalid
              ? "Enter a whole number of at least 1."
              : "A longer list is refused before the first item runs — never quietly cut short. Raise this deliberately for a bigger batch."}
          </div>

          <label className="field">
            <span className="label">Which column identifies an item?</span>
            <select
              className="select"
              value={identityColumn}
              disabled={collectionColumns.length === 0 && identityColumn === ""}
              onChange={(e) => update({ identity_column: e.target.value })}
            >
              <option value="">
                {collectionColumns.length === 1
                  ? `Work it out (there is only ${collectionColumns[0]})`
                  : "Work it out from the list"}
              </option>
              {/* A chosen column the picker cannot offer is still SHOWN as the
                  selection, or the control would silently read as "nothing
                  chosen" while the step is bound — and a stale choice would be
                  unreadable except by selecting it away. */}
              {identityMissing || identityUnverified ? (
                <option value={identityColumn}>
                  {identityMissing ? `${identityColumn} — no longer in the list` : identityColumn}
                </option>
              ) : null}
              {collectionColumns.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
          <div className="help">
            {identityMissing ? (
              <>
                <strong>{identityColumn}</strong> is not in the list any more — the step feeding this loop has changed
                shape. Pick again, or the run will stop before the first item.
              </>
            ) : identityUnverified ? (
              <>
                Each item is identified by <strong>{identityColumn}</strong>. Run the step feeding this loop once to
                see the rest of its columns here.
              </>
            ) : !columnsKnown ? (
              collectionLabel
                ? "Run the step feeding this loop once and its columns will appear here to choose from."
                : "Wire the list into this step on the canvas, then choose the column that tells one item from another."
            ) : identityColumn ? (
              "Each item's rows and each run-log row are stamped with this column's value, so you can tell whose is whose. Values that repeat make that stamp ambiguous — the run will say so."
            ) : (
              "Left to work itself out, a list with one column uses that column and a wider list numbers the items 1, 2, 3 — which says nothing about what they are. Choose a column instead."
            )}
          </div>

          <label className="field">
            <span className="label">When an item fails</span>
            <select
              className="select"
              value={onItemError}
              onChange={(e) => update({ on_item_error: e.target.value })}
            >
              <option value="continue">Carry on to the next item</option>
              <option value="halt">Stop the whole run</option>
            </select>
          </label>
          <div className="help">
            {onItemError === "continue"
              ? "Failed items are reported by name in the run log and the good ones still finish."
              : "Stopping still leaves the items already processed behind, and discards the results of the rest."}
          </div>

          {onItemError === "continue" ? (
            <>
              <label className="field">
                <span className="label">Give up after this many failures in a row</span>
                <input
                  className="input"
                  type="number"
                  min={1}
                  step={1}
                  value={abortAfter}
                  onChange={(e) => {
                    const v = Number(e.target.value.trim());
                    update({ abort_after_consecutive_failures: Number.isFinite(v) ? v : 3 });
                  }}
                />
              </label>
              <div className="help">
                Stops a systematically broken loop from working through the whole list. There is no off switch — a
                very high number is effectively off.
              </div>
            </>
          ) : null}
        </div>
      </div>

      <div className="card">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <strong>Running one item</strong>
          {/* Labelled for what it ACTUALLY does. "Run" on a loop start would
              read as "run the loop", and it is not — it is one item. */}
          <button
            className="btn btnSm"
            type="button"
            disabled={runBusy || operationStatus === "validate" || !collectionLabel}
            onClick={() => void onRunNode(selectedNode.id)}
            title={
              operationStatus === "validate"
                ? "Operation requires validation"
                : !collectionLabel
                  ? "Connect the list this loop iterates first"
                  : "Process the first item only, so you can see what the steps inside receive"
            }
          >
            {runBusy ? "Running…" : "Run one item"}
          </button>
        </div>
        <p className="muted" style={{ margin: 0, fontSize: 12 }}>
          This processes the FIRST item only, so you can see what the steps inside receive. Run the loop end to
          process every item. Steps inside a loop do not keep their own results — each one belongs to a single item —
          so a single-item run is how you inspect them.
        </p>
      </div>

      {nodeOutputRecord ? <NodeOutputStats rec={nodeOutputRecord} /> : null}
    </div>
  );
}
